import { api, tagTypes } from "./api";
import type {
  Advertisement,
  UploadFormData,
  AdvertisementUpdatePayload,
  AdAnalytics,
  SingleAdAnalytics,
} from "../features/adsSlice";

export interface GetAllAdsParams {
  page: number;
  adType: string;
  limit: number;
}

export interface GetAllAdsResponse {
  payload: {
    data: Advertisement[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface GetSingleAdResponse {
  payload: AdAnalytics;
}

export interface SingleGetSingleAdResponse {
  payload: SingleAdAnalytics;
}

// export interface GetSingleAdResponse {
// payload: {
// 	ad: Advertisement;
// };
// }

export interface ExportAdsResponse {
  payload: {
    data: {
      fileUrl: string;
    };
  };
  message: string;
}

export interface ToggleSuspendParams {
  ad_id: string;
}

export interface DeleteAdParams {
  ad_id: string;
}

// Inject ads endpoints into the main API
export const adsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all ads with pagination and filtering
    getAllAds: builder.query<GetAllAdsResponse, GetAllAdsParams>({
      query: ({ page = 1, adType, limit = 5 }) => ({
        url: `admin/v1/ads/data?adType=${adType}&page=${page}&limit=${limit}`,
        method: "GET",
      }),
    }),

    // Get single ad by ID
    getSingleAd: builder.query<GetSingleAdResponse, string>({
      query: (id) => ({
        url: `admin/v1/ads/data-id?ad_id=${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: tagTypes.Ad, id }],
    }),

    // New API
    getSingleAdinfo: builder.query<SingleGetSingleAdResponse, string>({
      query: (id) => ({
        url: `admin/v1/ads/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: tagTypes.Ad, id }],
    }),

    // Export ads
    exportAds: builder.query<ExportAdsResponse, string>({
      query: (adType = "") => ({
        url: `admin/v1/ads/export?adType=${adType}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Ad],
    }),

    // Upload new ad
    uploadAd: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "admin/v1/ads/upload",
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: [tagTypes.Ad],
    }),

    // Update existing ad
    updateAd: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "admin/v1/ads/update",
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: [tagTypes.Ad],
    }),

    // Toggle suspend status
    toggleSuspend: builder.mutation<any, ToggleSuspendParams>({
      query: ({ ad_id }) => ({
        url: `admin/v1/ads/toggle-suspend-status/one?ad_id=${ad_id}`,
        method: "PUT",
        body: { ad_id },
      }),
      invalidatesTags: [tagTypes.Ad],
    }),

    // Delete ad (soft delete)
    deleteAd: builder.mutation<any, DeleteAdParams>({
      query: ({ ad_id }) => ({
        url: `admin/v1/ads/soft-delete/one?ad_id=${ad_id}`,
        method: "PUT",
      }),
      invalidatesTags: [tagTypes.Ad],
    }),
  }),
});

export const {
  useGetAllAdsQuery,
  useLazyGetAllAdsQuery,
  useGetSingleAdQuery,
  useLazyGetSingleAdQuery,
  useGetSingleAdinfoQuery,
  useLazyGetSingleAdinfoQuery,
  useExportAdsQuery,
  useLazyExportAdsQuery,
  useUploadAdMutation,
  useUpdateAdMutation,
  useToggleSuspendMutation,
  useDeleteAdMutation,
} = adsApi;
